import React from 'react';
import CustomTabs from '@/components/CustomTabs';
import { Collapse, Empty, Tag } from 'antd';
import LufthansaCapabilities, { ProductGroup } from './LufthansaCapabilities';
import { isOpenNow } from './AirFranceTabs';

/* ------------------------- TYPES ------------------------- */

type SimpleDays = Record<string, string>;

type MatrixDays = Record<string, Record<string, string>>;

type GroupedDays = Record<string, string>;

type OperationSection =
  | {
      type: 'simple';
      days: SimpleDays;
    }
  | {
      type: 'matrix';
      columns: string[];
      days: MatrixDays;
    }
  | {
      type: 'grouped';
      label: string;
      days: GroupedDays;
    };

type OperationHours = Record<string, OperationSection>;

export interface LufthansaTabsProps {
  operationHours?: OperationHours;
  products?: ProductGroup[]; // <-- FIXED
}

/* --------------------------------------------------------- */

const LufthansaTabs: React.FC<LufthansaTabsProps> = ({
  operationHours,
  products,
}) => {
  /* ----------------- RENDER HELPERS ----------------- */

  const renderSimpleHours = (days: SimpleDays) => {
    return (
      <div className='hours-simple'>
        {Object.entries(days).map(([day, time]) => (
          <div className='row' key={day}>
            <div className='day'>{day}</div>
            {/* <div className='time'>{time}</div> */}
            <div className='zl-badge-item'>
              <time className='list-item'>{time}</time>
              {isOpenNow(day, time) && (
                <Tag
                  style={{
                    backgroundColor: 'var(--thin-green)',
                    borderColor: 'var(--thin-green)',
                    color: 'var(--green-900)',
                  }}
                >
                  Open now
                </Tag>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMatrixHours = (days: MatrixDays, columns: string[]) => {
    return (
      <div className='hours-matrix'>
        <div className='matrix-header'>
          <div></div>
          {columns.map((col) => (
            <div key={col} className='col-header'>
              {col}
            </div>
          ))}
        </div>

        {Object.entries(days).map(([day, values]) => (
          <div className='matrix-row' key={day}>
            <div className='day'>{day}</div>
            {columns.map((col) => (
              <div key={col} className='time'>
                {values[col]}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderGroupedHours = (days: GroupedDays, label: string) => {
    return (
      <div className='hours-grouped'>
        <div className='group-wrapper'>
          <div className='group-row'>
            <div className='group-label'></div>
            <div className='group-label'>{label}</div>
          </div>
        </div>

        {Object.entries(days).map(([day, time]) => (
          <div className='row' key={day}>
            <div className='day'>{day}</div>
            <div className='time'>{time}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderOperationSection = (section: OperationSection) => {
    switch (section.type) {
      case 'simple':
        return renderSimpleHours(section.days);
      case 'matrix':
        return renderMatrixHours(section.days, section.columns);
      case 'grouped':
        return renderGroupedHours(section.days, section.label);
      default:
        return null;
    }
  };

  /* ----------------- LABEL FORMATTER ----------------- */

  const formatLabel = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  /* ----------------- TABS DATA ----------------- */

  const Lufthansaitems = Object.entries(operationHours || {}).map(
    ([key, section], index) => ({
      key: String(index),
      label: formatLabel(key),
      children: renderOperationSection(section),
    })
  );
  const pageTabs = [
    {
      key: '1',
      label: `Overview`,
      children: (
        <>
          <div className='zl-heading-16 mb-3'>Operating hours</div>
          {Object.keys(operationHours ?? {}).length > 0 ? (
            <Collapse
              className='zl-custom-collapse Lufthansa-tabs'
              items={Lufthansaitems}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </>
      ),
    },
    {
      key: '2',
      label: `Capabilities`,
      children: (
        <>
          <div className='zl-heading-16 mb-3'>Products</div>
          {products?.length > 0 ? (
            <LufthansaCapabilities products={products} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </>
      ),
    },
  ];

  /* ----------------- RENDER ----------------- */

  return (
    <div>
      <div className='zl-custom-tabs' style={{ marginTop: '10px' }}>
        <CustomTabs items={pageTabs} />
      </div>
    </div>
  );
};

export default LufthansaTabs;
